/*
 * Writes TalonOne per-line attributes (`CartView.lineAttributes`) back onto cart
 * line properties. Originated from Teifi; now maintained in-theme.
 * An empty value clears a key; the proxy sends that clear on the compute after a
 * set, so a set → clear round trip settles in one pass each.
 *
 * Each `talon:cartview` merges its entries into the pending set by line key (newer
 * wins) and arms a delay past talon-one-cart's own attribute write; syncs run one
 * at a time.
 * `__T1_QUEUE_LINE_ATTRS__(view)` / `__T1_FLUSH_LINE_ATTRS__()` let
 * cart-promo-guard ship the write before checkout. No-op without `lineAttributes`.
 */
;(() => {
  if (window.__TEIFI_T1_LINE_ATTRIBUTES__) {
    return
  }

  window.__TEIFI_T1_LINE_ATTRIBUTES__ = true

  const root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    '/'
  // Past talon-one-cart's 600 ms attribute-write debounce.
  const SYNC_DELAY_MS = 700
  const RETRY_429_MS = 1000
  // Same cap talon-one-cart puts on its attribute-write chain.
  const REQUEST_TIMEOUT_MS = 8000

  let pending = null
  let timer = null
  let chain = Promise.resolve()
  const asString = (value) =>
    typeof value === 'string' ? value : JSON.stringify(value)

  function mergedProperties(line, attributes) {
    const properties = { ...(line.properties || {}) }
    let changed = false
    let cleared = null

    for (const [key, value] of Object.entries(attributes || {})) {
      if (value === '' || value == null) {
        if (properties[key] != null && properties[key] !== '') {
          delete properties[key]
          cleared = key
          changed = true
        }
      } else if (asString(properties[key]) !== asString(value)) {
        properties[key] = asString(value)
        changed = true
      }
    }

    // Shopify ignores `properties: {}`; a single blank key is what clears the last one.
    if (cleared && !Object.keys(properties).length) {
      properties[cleared] = ''
    }

    return changed ? properties : null
  }

  const sameProperties = (a, b) =>
    JSON.stringify(Object.entries(a || {}).sort()) ===
    JSON.stringify(Object.entries(b || {}).sort())

  const sellingPlanId = (item) =>
    item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id

  // Shopify merges lines with identical variant + plan + properties, folding a gift into a paid line.
  const wouldMerge = (cart, line, properties) =>
    cart.items.some(
      (item) =>
        item.key !== line.key &&
        item.variant_id === line.variant_id &&
        sellingPlanId(item) === sellingPlanId(line) &&
        sameProperties(item.properties, properties)
    )

  async function cartRequest(path, body, retried) {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })

    if (response.status === 429 && !retried) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_429_MS))
      return cartRequest(path, body, true)
    }

    if (!response.ok) {
      throw new Error(`${path} returned ${response.status}`)
    }

    return response.json()
  }

  // Same targets and shape as WithShopifyCart: <app-provider> listens on itself, the rest on document.
  function emit(name, detail) {
    ;(document.querySelector('app-provider') || document).dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true, detail })
    )
  }

  async function sync(entries) {
    const sections = Array.from(
      document.querySelectorAll('[register-for-cart-updates][section-id]'),
      (el) => el.getAttribute('section-id')
    )
      .slice(0, 5)
      .join(',')
    let cart = await cartRequest('cart.js')
    let written = null

    for (const entry of entries) {
      const line = cart.items.find((item) => item.key === entry.lineKey)
      const properties = line && mergedProperties(line, entry.attributes)

      if (!properties) {
        continue
      }

      if (wouldMerge(cart, line, properties)) {
        console.warn(
          '[teifi-t1-line-attributes] write would merge',
          entry.lineKey,
          'into another line'
        )
        continue
      }

      try {
        // change.js without `quantity` resets the line to 1.
        cart = await cartRequest('cart/change.js', {
          id: entry.lineKey,
          quantity: line.quantity,
          properties,
          sections,
          sections_url: window.location.pathname,
        })
        written = cart
      } catch (error) {
        console.warn('[teifi-t1-line-attributes]', error)
      }
    }

    if (written) {
      emit('cart:updated', written)
      // Mirrors the theme: one rendered section repaints, its consumer skips a null id; all-null is a failed render.
      const sectionsHtml = written.sections && Object.values(written.sections)
      if (
        sectionsHtml &&
        sectionsHtml.some((html) => typeof html === 'string' && html.trim())
      ) {
        emit('cart:updated-sections', written.sections)
      }
    }
  }

  // A view without entries (or a failed compute's null) must not drop what an earlier view queued.
  function queue(view) {
    const entries = view && view.lineAttributes
    if (!Array.isArray(entries) || !entries.length) {
      return false
    }
    pending = {
      ...pending,
      ...Object.fromEntries(entries.map((entry) => [entry.lineKey, entry])),
    }
    clearTimeout(timer)
    timer = null
    return true
  }

  function flush() {
    clearTimeout(timer)
    timer = null
    const entries = pending && Object.values(pending)
    pending = null
    if (entries) {
      chain = chain
        .then(() => sync(entries))
        .catch((error) => console.warn('[teifi-t1-line-attributes]', error))
    }
    return chain
  }

  // The guard flushes itself; a debounced write must not land inside its own cart writes.
  window.__T1_QUEUE_LINE_ATTRS__ = (view) => {
    clearTimeout(timer)
    timer = null
    return queue(view)
  }
  window.__T1_FLUSH_LINE_ATTRS__ = flush

  document.addEventListener('talon:cartview', (event) => {
    if (queue(event.detail)) {
      timer = setTimeout(flush, SYNC_DELAY_MS)
    }
  })
})()
